<template>
  <div>
    <p class="my-5">
      Sometimes a formal letter is required (or what an employer requests). You can generate a
      formal application letter by completing the form below.
    </p>

    <form id="applicationLetter" class="mt-5" @submit.prevent="submitApplicationLetter(true)">
      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2 w-full">
          <label>
            Your Name
            <vs-input
              class="w-full"
              v-model="applicationLetter.name"
              autocomplete="name"
              @change="handleBlur"
            />
          </label>
        </div>
      </div>
      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2 w-full">
          <label>
            Street Address
            <vs-input
              class="w-full"
              v-model="applicationLetter.street"
              autocomplete="address-line1"
              @change="handleBlur"
            />
          </label>
        </div>
      </div>
      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2 w-full">
          <label>
            Town
            <vs-input
              class="w-full"
              v-model="applicationLetter.town"
              autocomplete="address-level2"
              @change="handleBlur"
            />
          </label>
        </div>
      </div>
      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2 w-full">
          <label>
            State
            <vs-input
              class="w-full"
              v-model="applicationLetter.state"
              autocomplete="address-level1"
              @change="handleBlur"
            />
          </label>
        </div>
      </div>
      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2 w-full">
          <label>
            Postcode
            <vs-input
              class="w-full"
              v-model="applicationLetter.postcode"
              autocomplete="postal-code"
              @change="handleBlur"
            />
          </label>
        </div>
      </div>
      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2 w-full">
          <label>
            Phone Number
            <vs-input
              class="w-full"
              v-model="applicationLetter.phone"
              autocomplete="tel-national"
              @change="handleBlur"
            />
          </label>
        </div>
      </div>
      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2 w-full">
          <label>
            Email Address
            <vs-input
              class="w-full"
              v-model="applicationLetter.email"
              autocomplete="email"
              @change="handleBlur"
            />
          </label>
        </div>
      </div>
      <div class="vx-row mb-10">
        <div class="vx-col sm:w-1/2 w-full">
          <label>
            Today's Date (example: {{ todayDate }})
            <vs-input class="w-full" v-model="applicationLetter.date" @change="handleBlur" />
          </label>
        </div>
      </div>
      <div class="vx-row mb-4 mt-5">
        <div class="vx-col sm:w-1/2 w-full focusable-parent">
          <label>
            Name and details of letter recipient
            <vs-textarea
              height="100px"
              class="w-full focus-visible-style"
              v-model="applicationLetter.recipient"
              @change="submitApplicationLetter(false)"
            />
          </label>
        </div>
      </div>
      <div class="vx-row mb-4">
        <div class="vx-col sm:w-1/2 w-full">
          <label>
            Who is this letter addressed to?
            <vx-input-group class="mb-base">
              <template slot="prepend">
                <div class="prepend-text">
                  <span class="text-black">Dear</span>
                </div>
              </template>
              <vs-input v-model="applicationLetter.addressed_to" @change="handleBlur" />
            </vx-input-group>
          </label>
        </div>
      </div>
      <div class="vx-row mb-4">
        <div class="vx-col sm:w-3/4 w-full focusable-parent">
          <label>
            Letter Body
            <vs-textarea
              height="300px"
              class="w-full focus-visible-style"
              v-model="applicationLetter.letter_body"
              @change="submitApplicationLetter(false)"
            />
          </label>
        </div>
      </div>
      <div class="vx-row mb-4">
        <div class="vx-col w-full">
          <span>Yours sincerely,</span>
        </div>
      </div>
      <div class="vx-row mb-10">
        <div class="vx-col sm:w-1/2 w-full">
          <label>
            Your Name
            <vs-input class="w-full" v-model="applicationLetter.signature" @change="handleBlur" />
          </label>
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col w-full">
          <button class="btn btn-primary btn-size-medium mr-3 mb-2" button="submit">Save</button>
          <button
            class="btn btn-secondary btn-size-medium"
            id="view-application-letter-btn"
            @click="downloadApplicationLetter"
            v-if="canDownload"
          >
            View Application Letter
          </button>
          <vs-progress indeterminate color="primary" v-if="downloadingAppLetter"></vs-progress>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import store from '@/store/store';
import theApi from '@/mixins/theApi';

export default {
  computed: {
    prework() {
      return store.getters['authStudent/prework'];
    },
  },
  data() {
    return {
      todayDate: new Date().toLocaleDateString('en-AU'),
      applicationLetter: {
        name: '',
        street: '',
        town: '',
        state: '',
        postcode: '',
        phone: '',
        email: '',
        date: '',
        recipient: '',
        addressed_to: '',
        letter_body: '',
        signature: '',
      },
      canDownload: false,
      downloadingAppLetter: false,
    };
  },
  methods: {
    async submitApplicationLetter(formSubmitted = false) {
      const formData = new FormData();
      for (const [key, value] of Object.entries(this.applicationLetter)) {
        formData.append(key, value);
      }
      theApi.methods
        .makeCall(
          false,
          'apply/application_letter/save',
          'post',
          formData,
          formSubmitted ? 'Application Letter saved successfully.' : null,
          true,
        )
        .then(
          () => {
            const data = {
              status_section: 'section_apply',
              section: 'application_letter',
              data: this.applicationLetter,
              application_type: 1,
            };
            store.dispatch('authStudent/completePreworkSection', data);
            this.canDownload = true;
          },
          () => {},
        );
    },
    downloadApplicationLetter() {
      const formData = new FormData();
      for (const [key, value] of Object.entries(this.applicationLetter)) {
        formData.append(key, value);
      }
      theApi.methods
        .makeCall(false, 'apply/application_letter/save', 'post', formData, null, true)
        .then(
          () => {
            const data = {
              status_section: 'section_apply',
              section: 'application_letter',
              data: this.applicationLetter,
              application_type: 1,
            };
            store.dispatch('authStudent/completePreworkSection', data);

            this.downloadingAppLetter = true;
            theApi.methods.makeCall(false, 'apply/application_letter/download', 'get').then(
              (data) => {
                if (data.status == 'OK') {
                  store.dispatch('popup/showIframePopup', data.url);
                  store.commit('popup/setFocusElemOnPopupClose', 'view-application-letter-btn');

                  this.downloadingAppLetter = false;
                }
              },
              () => {
                this.downloadingAppLetter = false;
              },
            );
          },
          () => {},
        );
    },
    handleBlur(e) {
      if (e.target.value) {
        this.submitApplicationLetter();
      }
    },
  },
  mounted() {
    if (this.prework.application_letter) {
      this.applicationLetter = this.prework.application_letter;
      this.canDownload = true;
    }
  },
  watch: {
    prework() {
      if (this.prework.application_letter) {
        this.applicationLetter = this.prework.application_letter;
        this.canDownload = true;
      }
    },
  },
  mixins: [theApi],
};
</script>

<style lang="scss" scoped>
.prepend-text {
  border: 1px solid #cccccc;
  border-right: none;
}
</style>
